/**
 * F37 (Wave 2D) — curated badge-icon picker (D13).
 *
 * A grid of design-token-icon choices; the selected one is highlighted. This is
 * NOT a file upload — `badge_catalog.icon_code` references a
 * `@bravemove/design-tokens/icons` component, so the admin picks from a curated set
 * (`BADGE_ICON_CHOICES`). Composes the `BadgeIcon` resolver for each preview.
 */
import { useId } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { BadgeIcon } from './BadgeIcon';
import { BADGE_ICON_CHOICES } from './badgeIcons';

export interface BadgeIconPickerProps {
  readonly value: string;
  readonly onChange: (code: string) => void;
  readonly disabled?: boolean;
}

export function BadgeIconPicker({ value, onChange, disabled = false }: BadgeIconPickerProps) {
  const { t } = useI18n();
  const groupId = useId();
  return (
    <div
      role="radiogroup"
      aria-label={t('recommendation:catalog.icon_label', { defaultValue: 'Icon' })}
      className="grid grid-cols-6 gap-2"
      data-testid="badge-icon-picker"
    >
      {BADGE_ICON_CHOICES.map((choice) => {
        const selected = choice.code === value;
        return (
          <button
            key={choice.code}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={choice.label}
            title={choice.label}
            disabled={disabled}
            id={`${groupId}-${choice.code}`}
            data-testid={`badge-icon-choice-${choice.code}`}
            onClick={() => onChange(choice.code)}
            className={[
              'flex aspect-square items-center justify-center rounded-md border transition-colors',
              'focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              selected
                ? 'border-accent bg-accent/10 text-accent'
                : 'border-hairline bg-surface text-ink-2 hover:border-accent/50 hover:text-ink',
            ].join(' ')}
          >
            <BadgeIcon code={choice.code} size={18} />
          </button>
        );
      })}
    </div>
  );
}
